import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF, PAYMENT_LABEL, shortTag } from "@/utils/format";
import { TrendingUp, Wallet, ShoppingBag, Percent, Download, Play, AlertOctagon, Loader2, CheckCircle2, HandCoins, XCircle, BarChart3, Mail } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { toast } from "sonner";
import UpcomingPayouts from "@/components/admin/UpcomingPayouts";
import PurgeTestDataCard from "@/components/admin/PurgeTestDataCard";

export default function AdminFinance() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [running, setRunning] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);
  const [debts, setDebts] = useState(null);
  const [suspensions, setSuspensions] = useState([]);
  const [diaspora, setDiaspora] = useState(null);

  const loadOverview = () => {
    apiClient.get("/admin/finance/overview").then(({ data }) => setOverview(data)).catch(() => {});
    apiClient.get("/admin/driver-debts").then(({ data }) => setDebts(data)).catch(() => {});
    apiClient.get("/admin/change-requests/suspensions").then(({ data }) => setSuspensions(data.items || [])).catch(() => {});
    apiClient.get("/admin/finance/diaspora").then(({ data }) => setDiaspora(data)).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    apiClient.get(`/admin/finance?days=${days}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { loadOverview(); }, []);

  const runPayouts = async () => {
    if (!confirm("Lancer maintenant les reversements MoMo pour tous les partenaires avec un solde positif ?")) return;
    setRunning(true);
    try {
      const { data } = await apiClient.post("/admin/payouts/run", {});
      toast.success(`Reversements OK · ${data.restaurants_paid} restos · ${data.drivers_paid} livreurs · ${data.skipped_no_account.length} sans compte`);
      loadOverview();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const sendWeeklyRecap = async () => {
    if (!confirm(t("admin.afRecapConfirm"))) return;
    setSendingRecap(true);
    try {
      const { data } = await apiClient.post("/admin/notifications/weekly-recap");
      toast.success(`Récap envoyé · ${data.drivers_sent} livreur(s) · ${data.restaurants_sent} restaurant(s)${data.skipped_already_sent ? ` · ${data.skipped_already_sent} déjà notifié(s) aujourd'hui` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingRecap(false);
    }
  };

  const sendMonthlyRecap = async () => {
    const month = new Date().toISOString().slice(0, 7);
    if (!confirm(`Envoyer par email le récapitulatif mensuel (${month}, PDF en pièce jointe) à tous les restaurants et livreurs actifs ce mois-ci ?`)) return;
    setSendingMonthly(true);
    try {
      const { data } = await apiClient.post("/admin/emails/monthly-recap", { month, force: true });
      toast.success(`Récap ${data.period_label} envoyé · ${data.restaurants} resto(s) · ${data.drivers} livreur(s)${data.skipped_no_email ? ` · ${data.skipped_no_email} sans email` : ""}${data.failed ? ` · ${data.failed} échec(s)` : ""}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingMonthly(false);
    }
  };

  if (loading || !data) return <div className="text-mute text-sm py-10">{t("admin.finance.loading")}</div>;

  const { totals, series, by_payment, by_zone, top_restaurants } = data;
  const aov = totals.orders ? Math.round(totals.revenue / totals.orders) : 0;
  const numberLocale = i18n.language === "en" ? "en-US" : "fr-FR";

  const exportCsv = () => {
    const rows = [
      [t("admin.finance.trend", { n: days }), t("admin.finance.colOrders"), t("admin.finance.colRevenue") + " (GNF)", t("admin.finance.colCommission") + " (GNF)"],
      ...series.map((d) => [d.date, d.orders, d.revenue, d.commission]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `neldaari_finance_${days}j.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-finance">
      {suspensions.length > 0 && (
        <div className="rounded-2xl bg-brand-red/10 border-2 border-brand-red/40 px-4 py-3" data-testid="finance-suspensions-alert">
          <div className="flex items-center gap-2 font-bold text-brand-red text-sm">
            <AlertOctagon className="w-5 h-5 shrink-0" />
            Reversements suspendus pour {suspensions.length} partenaire(s) — modification du compte de reversement en attente de validation
          </div>
          <div className="text-xs text-ink/80 mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {suspensions.map((s) => (
              <span key={s.request_id}>
                {s.partner_name}{s.partner_role === "driver" ? ` ${shortTag(s.user_id)}` : s.restaurant_name ? ` (${s.restaurant_name})` : ""} · {s.field_label}
              </span>
            ))}
          </div>
          <button
            onClick={() => navigate("/admin?tab=change-requests")}
            data-testid="finance-suspensions-link"
            className="mt-2 inline-flex items-center gap-1 rounded-full bg-brand-red text-white px-4 py-1.5 text-xs font-semibold"
          >
            Traiter les demandes
          </button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl font-semibold">{t("admin.finance.title")}</h2>
        <div className="flex items-center gap-2">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`finance-range-${d}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-all ${days === d ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand hover:border-brand-red"}`}
            >
              {t("admin.finance.days", { n: d })}
            </button>
          ))}
          <button onClick={exportCsv} data-testid="finance-export-csv" className="rounded-full bg-brand-red text-white px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> {t("admin.finance.csv")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label={t("admin.finance.kpiRevenue")} value={formatGNF(totals.revenue)} color="#E32636" testid="kpi-revenue" />
        <KpiCard icon={<Percent className="w-4 h-4" />} label={t("admin.finance.kpiCommission")} value={formatGNF(totals.commission)} color="#009E60" testid="kpi-commission" />
        <KpiCard icon={<ShoppingBag className="w-4 h-4" />} label={t("admin.finance.kpiOrders")} value={totals.orders.toLocaleString(numberLocale)} color="#F4C430" testid="kpi-orders" />
        <KpiCard icon={<Wallet className="w-4 h-4" />} label={t("admin.finance.kpiAov")} value={formatGNF(aov)} color="#1A1A1A" testid="kpi-aov" />
        <KpiCard icon={<AlertOctagon className="w-4 h-4" />} label="Dettes cash livreurs" value={formatGNF(debts?.total_debt || 0)} color="#E32636" testid="kpi-driver-debts" />
      </div>

      {/* Apurement des reliquats hérités (ancienne formule cash) */}
      <LegacyResidualsButton />

      {/* Payouts & ledger */}
      {overview && (
        <div className="bg-white border border-sand rounded-2xl p-5 space-y-4" data-testid="admin-finance-payouts">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-lg font-semibold">Reversements MoMo</h3>
              <p className="text-xs text-mute">Job automatique chaque lundi 08:00 (UTC). Récap mensuel emailé automatiquement le 1er du mois (07:00 UTC).</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={sendWeeklyRecap}
                disabled={sendingRecap}
                data-testid="admin-send-weekly-recap"
                className="inline-flex items-center gap-2 rounded-full border-2 border-ink text-ink bg-white hover:bg-elevated disabled:opacity-60 px-5 py-2.5 text-sm font-semibold"
              >
                {sendingRecap ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                Envoyer le récap hebdo
              </button>
              <button
                onClick={sendMonthlyRecap}
                disabled={sendingMonthly}
                data-testid="admin-send-monthly-recap"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand-green text-brand-green bg-white hover:bg-[#E7F6EE] disabled:opacity-60 px-5 py-2.5 text-sm font-semibold"
              >
                {sendingMonthly ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Récap mensuel par email (PDF)
              </button>
              <button
                onClick={runPayouts}
                disabled={running}
                data-testid="admin-run-payouts"
                className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Lancer les reversements maintenant
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatBox label="Solde plateforme (all-time)" value={formatGNF(overview.platform_balance.net)} color="#E32636" testid="stat-platform" />
            <StatBox label="À reverser (restos)" value={formatGNF(Math.max(0, overview.pending_restaurants_net))} color="#009E60" testid="stat-pending-restos" />
            <StatBox label="À reverser (livreurs)" value={formatGNF(Math.max(0, overview.pending_drivers_net))} color="#F4C430" testid="stat-pending-drivers" />
            <StatBox label="Litiges cash ouverts" value={overview.open_cash_disputes} color="#1A1A1A" testid="stat-cash-disputes" />
            <StatBox
              label={`Reversements express du jour (H+${overview.payout_delay_hours ?? 1})`}
              value={`${overview.express_today_count ?? 0} · ${formatGNF(overview.express_today_amount || 0)}`}
              color="#FF7900"
              testid="stat-express-today"
            />
          </div>
          {overview.express_failures && overview.express_failures.length > 0 && (
            <div className="rounded-2xl border-2 border-brand-red/30 bg-brand-red/5 p-4" data-testid="express-failures">
              <div className="flex items-center gap-2 mb-2">
                <AlertOctagon className="w-4 h-4 text-brand-red" />
                <h4 className="font-semibold text-sm">Reversements express en échec — à traiter ({overview.express_failures.length})</h4>
              </div>
              <p className="text-[11px] text-mute mb-2">Le job hebdomadaire du lundi rattrapera automatiquement ces reversements ; vous pouvez aussi corriger la cause (compte manquant…) ou lancer les reversements manuellement.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left uppercase tracking-widest text-mute">
                    <tr><th className="py-1.5">{t("admin.afThOrder")}</th><th>{t("admin.afThBeneficiary")}</th><th>{t("admin.afThAmount")}</th><th>{t("admin.afThReason")}</th><th>{t("admin.afThAttempts")}</th><th>{t("admin.afThLastTry")}</th></tr>
                  </thead>
                  <tbody>
                    {overview.express_failures.map((f) => (
                      <tr key={`${f.order_id}-${f.beneficiaire_id}`} className="border-t border-sand" data-testid={`express-failure-${f.order_id}`}>
                        <td className="py-1.5 font-mono">#{(f.order_id || "").slice(-6).toUpperCase()}</td>
                        <td><span className="uppercase text-mute mr-1">{f.beneficiaire_type}</span>{f.name || f.beneficiaire_id}</td>
                        <td className="font-semibold">{formatGNF(f.amount)}</td>
                        <td className="text-brand-red">{f.reason}</td>
                        <td>{f.attempts || 1}</td>
                        <td className="text-mute">{(f.last_attempt_at || "").slice(5, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {overview.recent_payouts && overview.recent_payouts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-widest text-mute">
                  <tr>
                    <th className="py-2">Date</th>
                    <th>{t("admin.afThBeneficiary")}</th>
                    <th>Montant</th>
                    <th>Commandes</th>
                    <th>{t("admin.afThReference")}</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-recent-payouts">
                  {overview.recent_payouts.slice(0, 10).map((p) => (
                    <tr key={p.payout_id || p.reference} className="border-t border-sand">
                      <td className="py-2">{(p.created_at || p.paid_at || "").slice(0, 10)}</td>
                      <td className="py-2">
                        <span className="text-xs uppercase tracking-widest text-mute mr-1">{p.beneficiaire_type || "?"}</span>
                        <span>{p.name || p.beneficiaire_id || "?"}</span>
                      </td>
                      <td className="py-2 font-semibold">{formatGNF(p.amount)}</td>
                      <td className="py-2">{p.orders_covered ?? "—"}</td>
                      <td className="py-2 font-mono text-xs">{p.reference}</td>
                      <td className="py-2">
                        {p.status === "paid" ? (
                          <span className="inline-flex items-center gap-1 text-brand-green text-xs font-semibold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Payé
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-brand-red text-xs font-semibold">
                            <AlertOctagon className="w-3.5 h-3.5" /> {p.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {diaspora && (
        <div className="bg-white border border-sand rounded-2xl p-5" data-testid="finance-diaspora-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs uppercase tracking-widest text-mute">🌍 Commandes diaspora (PayPal)</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl bg-elevated p-3">
              <div className="text-[10px] uppercase tracking-widest text-mute">Commandes</div>
              <div className="font-display text-2xl font-semibold" data-testid="diaspora-count">{diaspora.count}</div>
            </div>
            <div className="rounded-xl bg-elevated p-3">
              <div className="text-[10px] uppercase tracking-widest text-mute">Volume</div>
              <div className="font-display text-2xl font-semibold">{formatGNF(diaspora.volume_gnf)}</div>
              {diaspora.volume_eur > 0 && <div className="text-[11px] text-mute">≈ {diaspora.volume_eur.toFixed(2)} EUR encaissés</div>}
            </div>
            <div className="rounded-xl bg-elevated p-3">
              <div className="text-[10px] uppercase tracking-widest text-mute">Panier moyen</div>
              <div className="font-display text-2xl font-semibold">{formatGNF(diaspora.avg_basket_gnf)}</div>
            </div>
            <div className="rounded-xl bg-elevated p-3">
              <div className="text-[10px] uppercase tracking-widest text-mute">Pays d'origine</div>
              {diaspora.countries.length === 0 ? (
                <div className="text-sm text-mute mt-1">—</div>
              ) : (
                <div className="text-xs mt-1 space-y-0.5">
                  {diaspora.countries.slice(0, 4).map((c) => (
                    <div key={c.country} className="flex justify-between gap-2"><span className="truncate">{c.country}</span><b>{c.count}</b></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <UpcomingPayouts />

      <CashRemittancesPanel />

      <DriverDebtsPanel debts={debts} onChanged={loadOverview} />

      <div className="bg-white border border-sand rounded-2xl p-4">
        <h3 className="font-semibold mb-3">{t("admin.finance.trend", { n: days })}</h3>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E32636" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#E32636" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="com" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#009E60" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#009E60" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE8E0" />
              <XAxis dataKey="date" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatGNF(v)} />
              <Legend />
              <Area type="monotone" dataKey="revenue" name={t("admin.finance.seriesRevenue")} stroke="#E32636" fill="url(#rev)" strokeWidth={2} />
              <Area type="monotone" dataKey="commission" name={t("admin.finance.seriesCommission")} stroke="#009E60" fill="url(#com)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-sand rounded-2xl p-4">
          <h3 className="font-semibold mb-3">{t("admin.finance.byPayment")}</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={by_payment.map((p) => ({ ...p, label: PAYMENT_LABEL[p.method] || p.method }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE8E0" />
                <XAxis type="number" fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="label" fontSize={11} width={120} />
                <Tooltip formatter={(v) => formatGNF(v)} />
                <Bar dataKey="revenue" fill="#E32636" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-sand rounded-2xl p-4">
          <h3 className="font-semibold mb-3">{t("admin.finance.topZones")}</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={by_zone}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE8E0" />
                <XAxis dataKey="zone" fontSize={11} interval={0} angle={-30} textAnchor="end" height={60} />
                <YAxis fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatGNF(v)} />
                <Bar dataKey="revenue" fill="#009E60" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-2xl overflow-hidden">
        <h3 className="font-semibold p-4 pb-2">{t("admin.finance.topRestaurants")}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-mute bg-elevated">
              <tr>
                <th className="text-left px-4 py-2">{t("admin.finance.colRestaurant")}</th>
                <th className="text-right px-4 py-2">{t("admin.finance.colOrders")}</th>
                <th className="text-right px-4 py-2">{t("admin.finance.colRevenue")}</th>
                <th className="text-right px-4 py-2">{t("admin.finance.colCommission")}</th>
              </tr>
            </thead>
            <tbody>
              {top_restaurants.map((r, i) => (
                <tr key={r.restaurant_id} className="border-t border-sand" data-testid={`top-rest-${i}`}>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5 text-right">{r.orders}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{formatGNF(r.revenue)}</td>
                  <td className="px-4 py-2.5 text-right text-brand-green">{formatGNF(r.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DriverShareCard />

      <RefundsCard />

      <CancelInDeliveryStatsCard />

      <PurgeTestDataCard />
    </div>
  );
}

/** Suivi des annulations en cours de livraison — détection des clients abusifs. */
function CancelInDeliveryStatsCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("/admin/finance/cancel-in-delivery-stats?days=90").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const REFUND_LABEL = { none: "Aucun", partial: "Partiel", full: "Complet" };

  const reload = () => apiClient.get("/admin/finance/cancel-in-delivery-stats?days=90").then(({ data }) => setData(data)).catch(() => {});

  const toggleCashRestriction = async (c) => {
    const next = !c.cash_restricted;
    if (!confirm(next
      ? `Interdire le paiement en espèces à ${c.name} ? Ses prochaines commandes devront être payées en ligne.`
      : `Réautoriser le paiement en espèces à ${c.name} ?`)) return;
    try {
      await apiClient.post(`/admin/users/${c.user_id}/restrict-cash`, { restricted: next });
      toast.success(next ? `${c.name} : cash interdit — paiement en ligne obligatoire` : `${c.name} : cash réautorisé`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white border border-sand rounded-2xl p-5" data-testid="cid-stats-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-display text-lg font-semibold">Annulations en cours de livraison</h3>
        {data && <span className="text-xs text-mute" data-testid="cid-stats-total">{data.total} sur 90 jours</span>}
      </div>
      <p className="text-sm text-mute mb-4">
        Fréquence par client et par zone — repérez les abus pour protéger vos livreurs.
      </p>
      {!data || data.total === 0 ? (
        <div className="text-sm text-mute" data-testid="cid-stats-empty">{t("admin.afCidEmpty")}</div>
      ) : (
        <div className="space-y-4">
          {/* Par client */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-mute font-semibold mb-2">Par client</div>
            <div className="space-y-1.5">
              {data.by_client.slice(0, 8).map((c) => (
                <div key={c.user_id} className="flex items-center justify-between gap-2 rounded-xl bg-elevated border border-sand px-3 py-2 text-sm" data-testid={`cid-client-${c.user_id}`}>
                  <div className="min-w-0">
                    <span className="font-semibold">{c.name}</span>
                    <span className="text-xs text-mute ml-2">{c.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.count >= 2 && (
                      <span className="inline-flex rounded-full bg-brand-red/10 text-brand-red px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`cid-abuse-${c.user_id}`}>
                        ⚠ Récidive ×{c.count}
                      </span>
                    )}
                    <span className="text-xs text-mute">{c.count} annulation(s) · {formatGNF(c.refunded_total)} remboursés</span>
                    <button
                      onClick={() => toggleCashRestriction(c)}
                      data-testid={`cid-restrict-${c.user_id}`}
                      className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                        c.cash_restricted
                          ? "bg-elevated border border-sand text-mute hover:border-ink"
                          : "bg-brand-red text-white hover:bg-[#C11E2C]"
                      }`}
                    >
                      {c.cash_restricted ? t("admin.afCashAllow") : t("admin.afCashForbid")}
                    </button>
                    {c.cash_restricted && (
                      <span className="inline-flex rounded-full bg-[#FFF3CD] text-[#946A00] px-2 py-0.5 text-[10px] font-bold uppercase" data-testid={`cid-restricted-${c.user_id}`}>
                        Cash interdit
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Par zone */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-mute font-semibold mb-2">Par zone</div>
            <div className="flex flex-wrap gap-1.5">
              {data.by_zone.map((z) => (
                <span key={z.zone} className="inline-flex rounded-full bg-elevated border border-sand px-2.5 py-1 text-xs font-semibold">
                  {z.zone} · {z.count}
                </span>
              ))}
            </div>
          </div>
          {/* Détail récent */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-mute border-b border-sand">
                  <th className="py-2 pr-3">Commande</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Zone</th>
                  <th className="py-2 pr-3">Montant</th>
                  <th className="py-2 pr-3">Remboursement</th>
                  <th className="py-2 pr-3">Motif</th>
                  <th className="py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.items.slice(0, 10).map((i) => (
                  <tr key={i.order_id} className="border-b border-sand/60 last:border-0" data-testid={`cid-row-${i.order_id}`}>
                    <td className="py-2 pr-3 font-mono text-xs">#{i.order_id.slice(-6).toUpperCase()}</td>
                    <td className="py-2 pr-3">{i.client_name}</td>
                    <td className="py-2 pr-3 text-xs">{i.zone}</td>
                    <td className="py-2 pr-3 font-semibold">{formatGNF(i.total)}</td>
                    <td className="py-2 pr-3 text-xs">{REFUND_LABEL[i.client_refund] || "—"}{i.client_refund_amount > 0 ? ` (${formatGNF(i.client_refund_amount)})` : ""}</td>
                    <td className="py-2 pr-3 text-xs text-mute max-w-[220px] truncate" title={i.reason}>{i.reason}</td>
                    <td className="py-2 text-xs text-mute">{(i.at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** Visibilité admin sur les remboursements automatiques (refus resto, timeout 15 min). */
function RefundsCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const load = () => {
    apiClient.get("/admin/finance/refunds").then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(load, []);

  const retry = async (orderId) => {
    setRetrying(orderId);
    try {
      await apiClient.post(`/admin/finance/refunds/${orderId}/retry`);
      toast.success(t("admin.afRefundRetried"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRetrying(null);
    }
  };

  const TRIGGER_LABEL = {
    restaurant: "Refus restaurant",
    client: "Annulation client",
    admin: "Annulation admin",
    system_timeout: "Timeout 15 min (sans réponse)",
  };

  return (
    <div className="bg-white border border-sand rounded-2xl p-5" data-testid="refunds-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-display text-lg font-semibold">{t("admin.afRefundsTitle")}</h3>
        {data && (
          <span className="text-xs text-mute" data-testid="refunds-total">
            Total remboursé : <b className="text-brand-red">{formatGNF(data.total_refunded)}</b>
            {data.failed_count > 0 && <span className="ml-2 text-brand-red font-semibold">· {data.failed_count} échec(s)</span>}
          </span>
        )}
      </div>
      <p className="text-sm text-mute mb-4">
        Déclenchés automatiquement : refus restaurant, annulation d&apos;une commande payée, ou restaurant sans réponse après 15 minutes.
      </p>
      {!data || data.items.length === 0 ? (
        <div className="text-sm text-mute" data-testid="refunds-empty">Aucun remboursement pour le moment.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-mute border-b border-sand">
                <th className="py-2 pr-3">Commande</th>
                <th className="py-2 pr-3">Montant</th>
                <th className="py-2 pr-3">Moyen</th>
                <th className="py-2 pr-3">Motif</th>
                <th className="py-2 pr-3">{t("admin.afThReference")}</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.refund_id} className="border-b border-sand/60 last:border-0" data-testid={`refund-row-${r.order_id}`}>
                  <td className="py-2 pr-3 font-mono text-xs">#{(r.order_id || "").slice(-6).toUpperCase()}</td>
                  <td className="py-2 pr-3 font-semibold">{formatGNF(r.amount)}</td>
                  <td className="py-2 pr-3 uppercase text-xs">{r.payment_method}{r.simulated ? " (simulé)" : ""}</td>
                  <td className="py-2 pr-3 text-xs">{TRIGGER_LABEL[r.triggered_by] || r.triggered_by} — {r.reason}</td>
                  <td className="py-2 pr-3 font-mono text-[11px]">{r.reference || "—"}</td>
                  <td className="py-2 pr-3">
                    {r.status === "refunded" ? (
                      <span className="inline-flex rounded-full bg-[#E7F6EE] text-brand-green px-2 py-0.5 text-[11px] font-bold">{t("admin.afRefunded")}</span>
                    ) : (
                      <button onClick={() => retry(r.order_id)} disabled={retrying === r.order_id}
                        data-testid={`refund-retry-${r.order_id}`}
                        className="inline-flex rounded-full bg-brand-red text-white px-2.5 py-0.5 text-[11px] font-bold disabled:opacity-50">
                        {retrying === r.order_id ? "…" : "Échec — relancer"}
                      </button>
                    )}
                  </td>
                  <td className="py-2 text-xs text-mute">{(r.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Apurement en un clic des reliquats de dette hérités de l'ancienne formule cash. */
function LegacyResidualsButton() {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/driver-debts/settle-legacy-residuals");
      toast.success(data.count > 0
        ? `${data.count} livreur(s) apuré(s) · ${formatGNF(data.total)} levés`
        : "Aucun reliquat hérité à apurer");
      setConfirming(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-sand rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="legacy-residuals-card">
      <div className="min-w-0">
        <div className="font-display font-semibold text-sm">Apurement des reliquats hérités</div>
        <p className="text-xs text-mute mt-0.5">Lève les résidus « cash à reverser » des livreurs ayant déjà régularisé avec l'ancienne formule. Les dettes récentes ne sont pas touchées.</p>
      </div>
      {!confirming ? (
        <button onClick={() => setConfirming(true)} data-testid="legacy-residuals-btn" className="rounded-full border border-brand-red text-brand-red px-4 py-2 text-sm font-semibold hover:bg-brand-red hover:text-white transition-colors">
          Apurer les reliquats
        </button>
      ) : (
        <div className="flex gap-2">
          <button onClick={run} disabled={busy} data-testid="legacy-residuals-confirm" className="rounded-full bg-brand-red text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {busy ? "Apurement…" : "Confirmer"}
          </button>
          <button onClick={() => setConfirming(false)} className="rounded-full border border-sand px-4 py-2 text-sm">Annuler</button>
        </div>
      )}
    </div>
  );
}

/** Réglage plateforme : part livreur (%) sur les frais de livraison. */
function DriverShareCard() {
  const { t } = useTranslation();
  const [pct, setPct] = useState(null);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/settings/driver-share").then(({ data }) => {
      setPct(data.pct);
      setInput(String(data.pct));
    }).catch(() => {});
  }, []);

  const save = async () => {
    const v = parseInt(input, 10);
    if (!v || v < 10 || v > 95) {
      toast.error(t("admin.afPctRange"));
      return;
    }
    if (!confirm(`Fixer la part livreur à ${v}% des frais de livraison ?\n\nAppliqué immédiatement : nouvelles commandes, gains affichés aux livreurs, et recalibrage des écritures encore en attente.`)) return;
    setSaving(true);
    try {
      const { data } = await apiClient.put("/admin/settings/driver-share", { pct: v });
      setPct(data.pct);
      toast.success(`Part livreur fixée à ${data.pct}% (${data.recalibrated_entries} écriture(s) en attente recalibrée(s))`, { duration: 8000 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-sand rounded-2xl p-5" data-testid="driver-share-card">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-4 h-4 text-brand-green" />
        <h3 className="font-display text-lg font-semibold">Part livreur sur les frais de livraison</h3>
      </div>
      <p className="text-sm text-mute mb-4">
        Pourcentage des frais de livraison reversé au livreur (le reste revient à la plateforme).
        Appliqué partout automatiquement : comptabilité des nouvelles commandes, gains affichés aux livreurs, dashboards.
        Les écritures <b>en attente</b> sont recalibrées au nouveau taux ; l&apos;historique déjà reversé n&apos;est jamais modifié.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <input
            type="number" min={10} max={95} value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="driver-share-input"
            className="w-24 bg-white border border-sand rounded-xl px-3 py-2.5 outline-none focus:border-brand-red font-semibold text-center"
          />
          <span className="text-sm font-semibold text-mute">%</span>
        </div>
        <button
          onClick={save}
          disabled={saving || pct === null || String(pct) === input}
          data-testid="driver-share-save"
          className="inline-flex items-center gap-2 rounded-full bg-ink hover:bg-black disabled:opacity-50 text-white px-5 py-2.5 text-sm font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Appliquer
        </button>
        {pct !== null && (
          <span className="text-xs text-mute" data-testid="driver-share-current">
            Taux actuel : <b className="text-brand-green">{pct}%</b> livreur / <b>{100 - pct}%</b> plateforme
          </span>
        )}
      </div>
    </div>
  );
}

function CashRemittancesPanel() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({});

  const load = () => {
    apiClient.get("/admin/remittances", { params: { status: "en_attente" } }).then(({ data }) => setItems(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (rid, action) => {
    let body = {};
    if (action === "reject") {
      const note = prompt("Motif du refus (visible par le livreur) :") || "";
      body = { note };
    }
    setBusy((b) => ({ ...b, [rid]: true }));
    try {
      await apiClient.post(`/admin/remittances/${rid}/${action === "reject" ? "reject" : "validate"}`, body);
      toast.success(action === "reject" ? t("admin.afDeclRejected") : t("admin.afRemValidated"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [rid]: false }));
    }
  };

  if (!items.length) return null;

  return (
    <div className="bg-white border-2 border-brand-yellow rounded-2xl p-5 space-y-3" data-testid="admin-cash-remittances">
      <div className="flex items-center gap-2">
        <HandCoins className="w-5 h-5 text-brand-green" />
        <h3 className="font-display text-lg font-semibold">{t("admin.afCashTitle")}</h3>
        <span className="rounded-full bg-brand-red text-white text-xs font-bold px-2 py-0.5">{items.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-mute">
            <tr>
              <th className="py-2">Date</th>
              <th>Livreur</th>
              <th>{t("admin.afThDeclared")}</th>
              <th>{t("admin.afThMomoRef")}</th>
              <th>Dette actuelle</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.remittance_id} className="border-t border-sand" data-testid={`remittance-${r.remittance_id}`}>
                <td className="py-2.5">{(r.declared_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="py-2.5 font-medium">{r.driver_name || r.driver_id}</td>
                <td className="py-2.5 font-semibold text-brand-green">{formatGNF(r.amount)}</td>
                <td className="py-2.5 font-mono text-xs">{r.momo_ref || r.reference || "—"}</td>
                <td className="py-2.5 font-semibold text-brand-red">{formatGNF(r.current_debt || 0)}</td>
                <td className="py-2.5">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => act(r.remittance_id, "validate")}
                      disabled={busy[r.remittance_id]}
                      data-testid={`remittance-validate-${r.remittance_id}`}
                      className="rounded-full bg-brand-green text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {busy[r.remittance_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Valider
                    </button>
                    <button
                      onClick={() => act(r.remittance_id, "reject")}
                      disabled={busy[r.remittance_id]}
                      data-testid={`remittance-reject-${r.remittance_id}`}
                      className="rounded-full border border-brand-red text-brand-red px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                    >
                      <XCircle className="w-3.5 h-3.5" /> Refuser
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DriverDebtsPanel({ debts, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState({});
  const [history, setHistory] = useState([]);

  const loadHistory = () => {
    apiClient.get("/admin/regularisations", { params: { limit: 8 } }).then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(() => { loadHistory(); }, [debts]);

  const settle = async (d) => {
    if (!confirm(`Confirmer la réception du virement MoMo de ${formatGNF(d.debt)} pour ${d.name} ?\nRéférence attendue : ${d.reference}`)) return;
    setBusy((b) => ({ ...b, [d.driver_id]: true }));
    try {
      await apiClient.post(`/admin/driver-debts/${d.driver_id}/settle`, {});
      toast.success(`Dette de ${d.name} régularisée — livreur ${d.blocked ? "débloqué" : "à jour"}`);
      onChanged();
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [d.driver_id]: false }));
    }
  };

  const remind = async (d) => {
    if (!confirm(`Envoyer un email de relance à ${d.name} pour sa dette de ${formatGNF(d.debt)} ?`)) return;
    setBusy((b) => ({ ...b, [`remind_${d.driver_id}`]: true }));
    try {
      const { data } = await apiClient.post(`/admin/driver-debts/${d.driver_id}/remind`);
      toast.success(`Relance envoyée à ${data.email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [`remind_${d.driver_id}`]: false }));
    }
  };

  const rows = debts?.drivers || [];

  return (
    <div className="bg-white border border-sand rounded-2xl p-5 space-y-4" data-testid="admin-driver-debts">
      <div className="flex items-center gap-2">
        <AlertOctagon className="w-5 h-5 text-brand-red" />
        <h3 className="font-display text-lg font-semibold">Dettes en cours</h3>
        {rows.length > 0 && <span className="rounded-full bg-brand-red text-white text-xs font-bold px-2 py-0.5">{rows.length}</span>}
        <span className="ml-auto text-sm font-semibold text-brand-red" data-testid="driver-debts-total">{formatGNF(debts?.total_debt || 0)}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-mute" data-testid="driver-debts-empty">Aucun livreur n'a de dette cash. 🎉</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-mute">
              <tr>
                <th className="py-2">Livreur</th>
                <th>{t("admin.thPhone")}</th>
                <th>Montant dû</th>
                <th>{t("admin.afThReference")}</th>
                <th>{t("admin.afThSeniority")}</th>
                <th>Statut</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.driver_id} className="border-t border-sand" data-testid={`debt-row-${d.driver_id}`}>
                  <td className="py-2.5 font-medium">{d.name}</td>
                  <td className="py-2.5">{d.phone || "—"}</td>
                  <td className="py-2.5 font-semibold text-brand-red">{formatGNF(d.debt)}</td>
                  <td className="py-2.5 font-mono text-xs">{d.reference}</td>
                  <td className="py-2.5">{d.age_days} j</td>
                  <td className="py-2.5">
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${d.blocked ? "bg-brand-red text-white" : "bg-[#E7F6EE] text-brand-green"}`}>
                      {d.blocked ? t("admin.afBlocked") : t("admin.afActive")}
                    </span>
                  </td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => remind(d)}
                        disabled={busy[`remind_${d.driver_id}`]}
                        data-testid={`debt-remind-${d.driver_id}`}
                        title="Envoyer un email de relance au livreur"
                        className="rounded-full border border-ink text-ink bg-white hover:bg-elevated px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                      >
                        {busy[`remind_${d.driver_id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                        Relancer par email
                      </button>
                      <button
                        onClick={() => settle(d)}
                        disabled={busy[d.driver_id]}
                        data-testid={`debt-settle-${d.driver_id}`}
                        className="rounded-full bg-brand-green text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                      >
                        {busy[d.driver_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Marquer comme régularisé
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {history.length > 0 && (
        <div className="pt-2 border-t border-sand">
          <div className="text-xs uppercase tracking-widest text-mute font-semibold mb-2">{t("admin.afLastRegs")}</div>
          <div className="space-y-1.5" data-testid="regularisations-history">
            {history.map((h) => (
              <div key={h.remittance_id} className="flex items-center gap-2 text-xs">
                <span className={`rounded-full px-2 py-0.5 font-bold ${h.source === "auto (MoMo)" ? "bg-brand-yellow/30 text-ink" : "bg-elevated text-mute"}`}>
                  {h.source === "auto (MoMo)" ? "auto (MoMo)" : "manuelle (admin)"}
                </span>
                <span className="font-medium">{h.driver_name}</span>
                <span className="font-semibold text-brand-green">{formatGNF(h.amount)}</span>
                <span className="font-mono text-mute truncate">{h.momo_ref || h.reference}</span>
                <span className="ml-auto text-mute">{(h.validated_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, color, testid }) {
  return (
    <div className="bg-white border border-sand rounded-2xl p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-mute mb-1 flex items-center gap-1.5" style={{ color }}>
        {icon} {label}
      </div>
      <div className="font-display text-xl sm:text-2xl font-semibold">{value}</div>
    </div>
  );
}

function StatBox({ label, value, color, testid }) {
  return (
    <div className="rounded-xl border border-sand bg-elevated p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-mute font-semibold">{label}</div>
      <div className="font-display text-lg sm:text-xl font-bold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

